import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Calendar,
  Search,
  Filter,
  Download,
  Eye,
  MapPin,
  Clock,
  Building2,
  User,
  ChevronLeft,
  ChevronRight,
  X,
  Play,
  Pause,
  Volume2,
  FileText,
  ThumbsUp,
  ThumbsDown,
  Mic
} from 'lucide-react';
import fieldTeamApi from '../../services/fieldTeamApi';
import './Meetings.css';

// Interest level options with colors
const INTEREST_OPTIONS = [
  { value: 'not_interested', label: 'Not Interested', color: '#ef4444', icon: ThumbsDown },
  { value: 'maybe_later', label: 'Maybe Later', color: '#f59e0b', icon: null },
  { value: 'interested', label: 'Interested', color: '#84cc16', icon: null },
  { value: 'very_interested', label: 'Very Interested', color: '#22c55e', icon: ThumbsUp },
  { value: 'ready_to_buy', label: 'Ready to Buy', color: '#3b82f6', icon: ThumbsUp }
];

// Outcome options
const OUTCOME_OPTIONS = [
  { value: 'all', label: 'All Outcomes' },
  { value: 'pending', label: 'Pending' },
  { value: 'scheduled_follow_up', label: 'Follow-up Scheduled' },
  { value: 'requested_trial', label: 'Requested Trial' },
  { value: 'requested_proposal', label: 'Requested Proposal' },
  { value: 'closed_deal', label: 'Closed Deal' },
  { value: 'lost', label: 'Lost' }
];

// Meeting type options
const MEETING_TYPES = [
  { value: 'first_contact', label: 'First Contact' },
  { value: 'follow_up', label: 'Follow-up' },
  { value: 'demo', label: 'Demo' },
  { value: 'negotiation', label: 'Negotiation' },
  { value: 'closing', label: 'Closing' },
  { value: 'other', label: 'Other' }
];

function Meetings() {
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, totalPages: 0 });

  // Filters
  const [dateFilter, setDateFilter] = useState('all'); // all, today, week, month
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [outcomeFilter, setOutcomeFilter] = useState('all');
  const [interestFilter, setInterestFilter] = useState('all');
  const [showAllMembers, setShowAllMembers] = useState(true);

  // Modal states
  const [selectedMeeting, setSelectedMeeting] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  // Audio player state
  const [playingAudio, setPlayingAudio] = useState(null);
  const [audioProgress, setAudioProgress] = useState(0);
  const audioRef = useRef(null);

  // Calculate date ranges
  const getDateRange = () => {
    const today = new Date();
    let start = null;
    let end = null;

    switch (dateFilter) {
      case 'today':
        start = today.toISOString().split('T')[0];
        end = start;
        break;
      case 'week':
        const weekAgo = new Date(today);
        weekAgo.setDate(weekAgo.getDate() - 7);
        start = weekAgo.toISOString().split('T')[0];
        end = today.toISOString().split('T')[0];
        break;
      case 'month':
        const monthAgo = new Date(today);
        monthAgo.setDate(monthAgo.getDate() - 30);
        start = monthAgo.toISOString().split('T')[0];
        end = today.toISOString().split('T')[0];
        break;
      case 'custom':
        start = startDate || null;
        end = endDate || null;
        break;
      default:
        break;
    }

    return { start, end };
  };

  // Fetch meetings
  const fetchMeetings = useCallback(async () => {
    setLoading(true);
    try {
      const { start, end } = getDateRange();
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        start_date: start || undefined,
        end_date: end || undefined,
        outcome: outcomeFilter !== 'all' ? outcomeFilter : undefined,
        interest: interestFilter !== 'all' ? interestFilter : undefined,
        all_members: showAllMembers ? 1 : undefined
      };

      const response = await fieldTeamApi.getMeetings(params);
      if (response.data.success) {
        setMeetings(response.data.data);
        setPagination(prev => ({
          ...prev,
          total: response.data.pagination.total,
          totalPages: response.data.pagination.totalPages
        }));
      }
    } catch (error) {
      console.error('Failed to fetch meetings:', error);
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, dateFilter, startDate, endDate, outcomeFilter, interestFilter, showAllMembers]);

  useEffect(() => {
    fetchMeetings();
  }, [fetchMeetings]);

  // View meeting details
  const handleViewMeeting = async (meeting) => {
    try {
      const response = await fieldTeamApi.getMeeting(meeting.id);
      if (response.data.success) {
        setSelectedMeeting(response.data.data);
        setShowDetailModal(true);
      }
    } catch (error) {
      console.error('Failed to fetch meeting details:', error);
    }
  };

  // Audio playback
  const handlePlayAudio = (audioUrl, meetingId) => {
    if (playingAudio === meetingId) {
      if (audioRef.current) {
        audioRef.current.pause();
      }
      setPlayingAudio(null);
    } else {
      // Stop previous audio
      if (audioRef.current) {
        audioRef.current.pause();
      }
      // Play new audio
      setPlayingAudio(meetingId);
      // In real implementation, you'd fetch the SAS URL first
      // For now, just set the playing state
    }
  };

  // Export to CSV
  const handleExport = () => {
    const headers = ['Date', 'Client', 'Member', 'Type', 'Interest', 'Outcome', 'Notes'];
    const rows = meetings.map(m => [
      formatDate(m.meeting_date),
      m.client_company_name || m.location_name || '-',
      m.member_name,
      m.meeting_type,
      m.client_interest,
      m.outcome,
      m.meeting_minutes || ''
    ]);

    const csv = [headers, ...rows].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `meetings_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  // Get interest badge
  const getInterestBadge = (interest) => {
    const option = INTEREST_OPTIONS.find(o => o.value === interest);
    if (!option) return <span className="interest-badge unknown">Unknown</span>;
    return (
      <span
        className="interest-badge"
        style={{ backgroundColor: `${option.color}15`, color: option.color, borderColor: `${option.color}30` }}
      >
        {option.label}
      </span>
    );
  };

  // Get outcome badge
  const getOutcomeBadge = (outcome) => {
    const colors = {
      pending: '#6b7280',
      scheduled_follow_up: '#8b5cf6',
      requested_trial: '#f59e0b',
      requested_proposal: '#3b82f6',
      closed_deal: '#22c55e',
      lost: '#ef4444'
    };
    const color = colors[outcome] || '#6b7280';
    return (
      <span
        className="outcome-badge"
        style={{ backgroundColor: `${color}15`, color: color }}
      >
        {outcome?.replace(/_/g, ' ')}
      </span>
    );
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric'
    });
  };

  // Format time
  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Format duration
  const formatDuration = (seconds) => {
    if (!seconds) return '';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Stats
  const stats = {
    total: pagination.total,
    interested: meetings.filter(m => ['interested', 'very_interested', 'ready_to_buy'].includes(m.client_interest)).length,
    closedDeals: meetings.filter(m => m.outcome === 'closed_deal').length,
    withMemos: meetings.filter(m => m.voice_memo_url).length
  };

  return (
    <div className="meetings-page">
      {/* Header */}
      <div className="field-team-page-header">
        <h1>Meetings</h1>
        <p>View and analyze all field team meetings</p>
      </div>

      {/* Stats */}
      <div className="stat-blocks">
        <div className="stat-block">
          <div className="stat-icon"><Calendar size={20} /></div>
          <div className="stat-content">
            <span className="stat-value">{stats.total}</span>
            <span className="stat-label">Total Meetings</span>
          </div>
        </div>
        <div className="stat-block">
          <div className="stat-icon"><ThumbsUp size={20} /></div>
          <div className="stat-content">
            <span className="stat-value">{stats.interested}</span>
            <span className="stat-label">Interested</span>
          </div>
        </div>
        <div className="stat-block">
          <div className="stat-icon"><Building2 size={20} /></div>
          <div className="stat-content">
            <span className="stat-value">{stats.closedDeals}</span>
            <span className="stat-label">Deals Closed</span>
          </div>
        </div>
        <div className="stat-block">
          <div className="stat-icon"><Mic size={20} /></div>
          <div className="stat-content">
            <span className="stat-value">{stats.withMemos}</span>
            <span className="stat-label">Voice Memos</span>
          </div>
        </div>
      </div>

      {/* Toolbar */}
      <div className="meetings-toolbar">
        <div className="toolbar-left">
          <div className="date-filter-group">
            <button
              className={`date-btn ${dateFilter === 'all' ? 'active' : ''}`}
              onClick={() => setDateFilter('all')}
            >
              All Time
            </button>
            <button
              className={`date-btn ${dateFilter === 'today' ? 'active' : ''}`}
              onClick={() => setDateFilter('today')}
            >
              Today
            </button>
            <button
              className={`date-btn ${dateFilter === 'week' ? 'active' : ''}`}
              onClick={() => setDateFilter('week')}
            >
              This Week
            </button>
            <button
              className={`date-btn ${dateFilter === 'month' ? 'active' : ''}`}
              onClick={() => setDateFilter('month')}
            >
              This Month
            </button>
            <button
              className={`date-btn ${dateFilter === 'custom' ? 'active' : ''}`}
              onClick={() => setDateFilter('custom')}
            >
              Custom
            </button>
          </div>

          {dateFilter === 'custom' && (
            <div className="date-inputs">
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
              <span>to</span>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
          )}

          <select
            className="filter-select"
            value={outcomeFilter}
            onChange={(e) => setOutcomeFilter(e.target.value)}
          >
            {OUTCOME_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>

          <select
            className="filter-select"
            value={interestFilter}
            onChange={(e) => setInterestFilter(e.target.value)}
          >
            <option value="all">All Interest Levels</option>
            {INTEREST_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>

        <div className="toolbar-right">
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={showAllMembers}
              onChange={(e) => setShowAllMembers(e.target.checked)}
            />
            All Members
          </label>
          <button className="btn btn-secondary" onClick={handleExport}>
            <Download size={14} />
            Export
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="meetings-table-container">
        <table className="meetings-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Client / Location</th>
              <th>Member</th>
              <th>Type</th>
              <th>Interest</th>
              <th>Outcome</th>
              <th>Voice Memo</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="8" className="loading-cell">Loading...</td>
              </tr>
            ) : meetings.length === 0 ? (
              <tr>
                <td colSpan="8" className="empty-cell">No meetings found</td>
              </tr>
            ) : (
              meetings.map((meeting) => (
                <tr key={meeting.id}>
                  <td>
                    <div className="date-cell">
                      <span className="meeting-date">{formatDate(meeting.meeting_date)}</span>
                      {meeting.started_at && (
                        <span className="meeting-time">{formatTime(meeting.started_at)}</span>
                      )}
                    </div>
                  </td>
                  <td>
                    <div className="client-cell">
                      {meeting.client_company_name ? (
                        <>
                          <Building2 size={14} />
                          <span className="client-name">{meeting.client_company_name}</span>
                        </>
                      ) : (
                        <>
                          <MapPin size={14} />
                          <span className="location-name">{meeting.location_name || meeting.address || 'No location'}</span>
                        </>
                      )}
                    </div>
                  </td>
                  <td>
                    <div className="member-cell">
                      <User size={14} />
                      <span>{meeting.member_name}</span>
                    </div>
                  </td>
                  <td className="type-cell">
                    {meeting.meeting_type?.replace(/_/g, ' ')}
                  </td>
                  <td>{getInterestBadge(meeting.client_interest)}</td>
                  <td>{getOutcomeBadge(meeting.outcome)}</td>
                  <td>
                    {meeting.voice_memo_url ? (
                      <button
                        className={`audio-btn ${playingAudio === meeting.id ? 'playing' : ''}`}
                        onClick={() => handlePlayAudio(meeting.voice_memo_url, meeting.id)}
                        title={meeting.voice_memo_duration_seconds ? `${formatDuration(meeting.voice_memo_duration_seconds)}` : 'Play'}
                      >
                        {playingAudio === meeting.id ? <Pause size={14} /> : <Play size={14} />}
                        {meeting.voice_memo_duration_seconds && (
                          <span className="duration">{formatDuration(meeting.voice_memo_duration_seconds)}</span>
                        )}
                      </button>
                    ) : (
                      <span className="no-memo">-</span>
                    )}
                  </td>
                  <td>
                    <button className="icon-btn" onClick={() => handleViewMeeting(meeting)} title="View Details">
                      <Eye size={14} />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className="pagination">
          <button
            className="pagination-btn"
            disabled={pagination.page === 1}
            onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
          >
            <ChevronLeft size={14} />
          </button>
          <span className="pagination-info">
            Page {pagination.page} of {pagination.totalPages}
          </span>
          <button
            className="pagination-btn"
            disabled={pagination.page === pagination.totalPages}
            onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
          >
            <ChevronRight size={14} />
          </button>
        </div>
      )}

      {/* Detail Modal */}
      {showDetailModal && selectedMeeting && (
        <div className="modal-overlay" onClick={() => setShowDetailModal(false)}>
          <div className="modal-content detail-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Meeting Details</h2>
              <button className="close-btn" onClick={() => setShowDetailModal(false)}>
                <X size={18} />
              </button>
            </div>
            <div className="modal-body">
              {/* Meeting Info */}
              <div className="detail-section">
                <h3>Meeting Information</h3>
                <div className="detail-grid">
                  <div className="detail-item">
                    <label>Date</label>
                    <span>{formatDate(selectedMeeting.meeting_date)}</span>
                  </div>
                  <div className="detail-item">
                    <label>Time</label>
                    <span>
                      {formatTime(selectedMeeting.started_at)}
                      {selectedMeeting.ended_at && ` - ${formatTime(selectedMeeting.ended_at)}`}
                    </span>
                  </div>
                  <div className="detail-item">
                    <label>Duration</label>
                    <span>{selectedMeeting.duration_minutes ? `${selectedMeeting.duration_minutes} mins` : '-'}</span>
                  </div>
                  <div className="detail-item">
                    <label>Type</label>
                    <span style={{ textTransform: 'capitalize' }}>{selectedMeeting.meeting_type?.replace(/_/g, ' ')}</span>
                  </div>
                  <div className="detail-item">
                    <label>Interest Level</label>
                    {getInterestBadge(selectedMeeting.client_interest)}
                  </div>
                  <div className="detail-item">
                    <label>Outcome</label>
                    {getOutcomeBadge(selectedMeeting.outcome)}
                  </div>
                </div>
              </div>

              {/* Visit Details */}
              {(selectedMeeting.contact_designation || selectedMeeting.contact_phone || selectedMeeting.visit_reason || selectedMeeting.visit_status_change) && (
                <div className="detail-section">
                  <h3>Visit Details</h3>
                  <div className="detail-grid">
                    {selectedMeeting.contact_designation && (
                      <div className="detail-item">
                        <label>Contact Designation</label>
                        <span>{selectedMeeting.contact_designation}</span>
                      </div>
                    )}
                    {selectedMeeting.contact_phone && (
                      <div className="detail-item">
                        <label>Phone / WhatsApp</label>
                        <span>{selectedMeeting.contact_phone}</span>
                      </div>
                    )}
                    {selectedMeeting.visit_reason && (
                      <div className="detail-item full-width">
                        <label>Reason for Visit</label>
                        <span>{selectedMeeting.visit_reason}</span>
                      </div>
                    )}
                    {selectedMeeting.visit_status_change && (
                      <div className="detail-item">
                        <label>Status Change</label>
                        <span style={{ padding: '2px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: 600,
                          color: '#3b82f6', background: 'rgba(59,130,246,0.15)' }}>
                          {selectedMeeting.visit_status_change.replace(/_/g, ' ')}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Client Info */}
              {selectedMeeting.client_company_name && (
                <div className="detail-section">
                  <h3>Client Information</h3>
                  <div className="detail-grid">
                    <div className="detail-item">
                      <label>Company</label>
                      <span>{selectedMeeting.client_company_name}</span>
                    </div>
                    <div className="detail-item">
                      <label>Contact</label>
                      <span>{selectedMeeting.client_contact_name || '-'}</span>
                    </div>
                    {selectedMeeting.client_contact_phone && (
                      <div className="detail-item">
                        <label>Phone</label>
                        <span>{selectedMeeting.client_contact_phone}</span>
                      </div>
                    )}
                    {selectedMeeting.client_contact_email && (
                      <div className="detail-item">
                        <label>Email</label>
                        <span>{selectedMeeting.client_contact_email}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Location */}
              <div className="detail-section">
                <h3>Location</h3>
                <div className="detail-grid">
                  {selectedMeeting.location_name && (
                    <div className="detail-item">
                      <label>Location Name</label>
                      <span>{selectedMeeting.location_name}</span>
                    </div>
                  )}
                  {selectedMeeting.address && (
                    <div className="detail-item full-width">
                      <label>Address</label>
                      <span>{selectedMeeting.address}</span>
                    </div>
                  )}
                  {selectedMeeting.floor && (
                    <div className="detail-item">
                      <label>Floor</label>
                      <span>{selectedMeeting.floor}</span>
                    </div>
                  )}
                  {selectedMeeting.office_number && (
                    <div className="detail-item">
                      <label>Office</label>
                      <span>{selectedMeeting.office_number}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Notes */}
              {selectedMeeting.meeting_minutes && (
                <div className="detail-section">
                  <h3>Meeting Notes</h3>
                  <p className="notes-text">{selectedMeeting.meeting_minutes}</p>
                </div>
              )}

              {/* Follow-up */}
              {selectedMeeting.follow_up_date && (
                <div className="detail-section">
                  <h3>Follow-up</h3>
                  <div className="detail-grid">
                    <div className="detail-item">
                      <label>Follow-up Date</label>
                      <span>{formatDate(selectedMeeting.follow_up_date)}</span>
                    </div>
                    {selectedMeeting.follow_up_notes && (
                      <div className="detail-item full-width">
                        <label>Follow-up Notes</label>
                        <span>{selectedMeeting.follow_up_notes}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Voice Memo */}
              {selectedMeeting.voice_memo_url && (
                <div className="detail-section">
                  <h3>Voice Memo</h3>
                  <div className="voice-memo-player">
                    <button
                      className={`play-btn ${playingAudio === selectedMeeting.id ? 'playing' : ''}`}
                      onClick={() => handlePlayAudio(selectedMeeting.voice_memo_url, selectedMeeting.id)}
                    >
                      {playingAudio === selectedMeeting.id ? <Pause size={20} /> : <Play size={20} />}
                    </button>
                    <div className="audio-info">
                      <span className="audio-duration">
                        {selectedMeeting.voice_memo_duration_seconds
                          ? formatDuration(selectedMeeting.voice_memo_duration_seconds)
                          : 'Voice Memo'}
                      </span>
                      <span className="audio-status">
                        {playingAudio === selectedMeeting.id ? 'Playing...' : 'Click to play'}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowDetailModal(false)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Meetings;
